import { ProcessedScanResult } from "../../components/Pages/Diary/SpaceScanner/SpaceScanner.types";
import { ScanDecision, UserPreference } from "./types/ScanHistory.types";

const MIN_MATCH_COUNT = 3;
const MIN_CONFIDENCE = 0.6;

interface DecisionGroup {
  key: string;
  decisions: ScanDecision[];
}

const groupBy = (
  decisions: ScanDecision[],
  keyFn: (d: ScanDecision) => string | undefined
): DecisionGroup[] => {
  const map = new Map<string, ScanDecision[]>();

  for (const d of decisions) {
    const key = keyFn(d);
    if (!key) continue;
    const group = map.get(key) ?? [];
    group.push(d);
    map.set(key, group);
  }

  return [...map.entries()].map(([key, items]) => ({
    key,
    decisions: items
  }));
};

const detectLabelAreaPreferences = (
  history: ScanDecision[]
): UserPreference[] => {
  const withLabels = history.filter((d) => d.labels.length > 0);
  const preferences: UserPreference[] = [];

  const groups = groupBy(withLabels, (d) => {
    const label = d.labels[0];
    return label && d.fromAreaName ? `${label}::${d.fromAreaName}` : undefined;
  });

  for (const group of groups) {
    const rejected = group.decisions.filter((d) => !d.accepted);
    if (rejected.length < MIN_MATCH_COUNT) continue;

    const confidence = rejected.length / group.decisions.length;
    if (confidence < MIN_CONFIDENCE) continue;

    const [label, areaName] = group.key.split("::");
    preferences.push({
      type: "label_area",
      description: `Keep ${label} bookings in ${areaName}`,
      confidence,
      matchCount: rejected.length,
      labels: [label],
      areaName
    });
  }

  return preferences;
};

const detectTypeAreaPreferences = (
  history: ScanDecision[]
): UserPreference[] => {
  const preferences: UserPreference[] = [];

  const groups = groupBy(history, (d) =>
    d.bookingType && d.toAreaName
      ? `${d.bookingType}::${d.toAreaName}`
      : undefined
  );

  for (const group of groups) {
    const accepted = group.decisions.filter((d) => d.accepted);
    if (accepted.length < MIN_MATCH_COUNT) continue;

    const confidence = accepted.length / group.decisions.length;
    if (confidence < MIN_CONFIDENCE) continue;

    const [bookingType, areaName] = group.key.split("::");
    preferences.push({
      type: "type_area",
      description: `Prefer ${areaName} for ${bookingType}`,
      confidence,
      matchCount: accepted.length,
      bookingType,
      areaName
    });
  }

  return preferences;
};

const detectAvoidAreaPreferences = (
  history: ScanDecision[]
): UserPreference[] => {
  const preferences: UserPreference[] = [];

  const groups = groupBy(history, (d) => d.toAreaName || undefined);

  for (const group of groups) {
    const rejected = group.decisions.filter((d) => !d.accepted);
    if (rejected.length < MIN_MATCH_COUNT) continue;

    const confidence = rejected.length / group.decisions.length;
    if (confidence < MIN_CONFIDENCE) continue;

    preferences.push({
      type: "avoid_area",
      description: `Avoid moving bookings to ${group.key}`,
      confidence,
      matchCount: rejected.length,
      areaName: group.key
    });
  }

  return preferences;
};

export const detectPreferences = (
  history: ScanDecision[]
): UserPreference[] => {
  if (history.length === 0) return [];

  return [
    ...detectLabelAreaPreferences(history),
    ...detectTypeAreaPreferences(history),
    ...detectAvoidAreaPreferences(history)
  ];
};

export const generatePreferenceExplanation = (
  preference: UserPreference
): string => {
  switch (preference.type) {
    case "label_area":
      return `I've noticed you prefer to keep ${preference.labels?.[0] ?? "labelled"} bookings at ${preference.areaName ?? "their current area"}, so I've prioritised that in this optimisation.`;
    case "type_area":
      return `I've noticed you prefer ${preference.areaName ?? "a specific area"} for ${preference.bookingType ?? "this booking type"}, so I've prioritised that in this optimisation.`;
    case "avoid_area":
      return `I've noticed you tend to avoid moving bookings to ${preference.areaName ?? "a specific area"}, so I've deprioritised that in this optimisation.`;
    case "keep_area":
      return `I've noticed you prefer to keep bookings at ${preference.areaName ?? "their current area"}, so I've prioritised that in this optimisation.`;
  }
};

export const applyPreferences = (
  results: ProcessedScanResult[],
  preferences: UserPreference[]
): { results: ProcessedScanResult[]; appliedCount: number } => {
  if (preferences.length === 0) {
    return { results, appliedCount: 0 };
  }

  let appliedCount = 0;

  const updatedResults = results.map((result) => {
    if (!result.moved) return result;

    for (const pref of preferences) {
      if (matchesRejectionPreference(result, pref)) {
        appliedCount++;
        return {
          ...result,
          moved: false,
          preferenceApplied: pref.description
        };
      }
    }

    return result;
  });

  return { results: updatedResults, appliedCount };
};

const matchesRejectionPreference = (
  result: ProcessedScanResult,
  preference: UserPreference
): boolean => {
  const booking = result.booking as typeof result.booking & {
    labels?: string[];
  };

  switch (preference.type) {
    case "label_area": {
      if (!preference.labels || preference.labels.length === 0) return false;
      const hasMatchingLabel = booking.labels?.some((l) =>
        preference.labels!.includes(l)
      );
      const isFromPreferredArea =
        result.previousAreas?.[0]?.name === preference.areaName;
      return Boolean(hasMatchingLabel && isFromPreferredArea);
    }
    case "avoid_area":
      return result.newAreas?.[0]?.name === preference.areaName;
    default:
      return false;
  }
};
